import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Divider,
} from "@mui/material";
import Grid from "./utils/CompatGrid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import { toast } from "react-toastify";
import {
  getProducts,
  getTemplates,
  resolvePlaceholders,
  generateLabel,
  recordPrint,
} from "./api/labelStudioApi";
import LabelCanvas from "./components/LabelCanvas";
import {
  exportSingleLabelPdf,
  exportCopiesAsA4Pdf,
  printLabelNode,
  printCopiesOnA4,
  computeA4Grid,
} from "./utils/pdfExport";

const GenerateLabel = () => {
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [productId, setProductId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [backgroundColorOverride, setBackgroundColorOverride] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [manufacturingDate, setManufacturingDate] = useState("");
  const [packedDate, setPackedDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [bestBeforeDate, setBestBeforeDate] = useState("");
  const [copies, setCopies] = useState(1);
  const [dataMap, setDataMap] = useState(null);
  const [generating, setGenerating] = useState(false);

  const previewNodeRef = useRef(null);

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .catch(() => setProducts([]));
    getTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId],
  );
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId],
  );

  const elements = useMemo(() => {
    if (!selectedTemplate?.elementsJson) return [];
    try {
      return JSON.parse(selectedTemplate.elementsJson);
    } catch (e) {
      return [];
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (!productId) {
      setDataMap(null);
      return;
    }
    resolvePlaceholders({
      productId,
      batchNumber,
      manufacturingDate: manufacturingDate || undefined,
      packedDate: packedDate || undefined,
      expiryDate: expiryDate || undefined,
      bestBeforeDate: bestBeforeDate || undefined,
    })
      .then((resolved) =>
        // Backend key names for batch fields can vary; ensure the exact
        // tokens used by templates ({{batch}}, {{manufacturing_date}}, etc.)
        // always resolve using the values already entered on this page.
        setDataMap({
          ...resolved,
          batch: resolved?.batch || batchNumber || "",
          manufacturing_date:
            resolved?.manufacturing_date || manufacturingDate || "",
          packed_date: resolved?.packed_date || packedDate || "",
          expiry: resolved?.expiry || expiryDate || "",
          best_before: resolved?.best_before || bestBeforeDate || "",
        }),
      )
      .catch(() => setDataMap(null));
  }, [
    productId,
    batchNumber,
    manufacturingDate,
    packedDate,
    expiryDate,
    bestBeforeDate,
  ]);

  const canGenerate = productId && templateId;

  // For an A4_SHEET template the grid is fixed by its configured
  // columns/rows; otherwise auto-fit as many copies of this template's
  // size as will fit on one A4 page.
  const a4GridOptions = useMemo(() => {
    if (!selectedTemplate) return {};
    if (selectedTemplate.paperType === "A4_SHEET") {
      return {
        columns: selectedTemplate.a4Columns || 2,
        rows: selectedTemplate.a4Rows || 5,
        marginXMm: selectedTemplate.a4MarginXMm ?? 5,
        marginYMm: selectedTemplate.a4MarginYMm ?? 5,
        spacingXMm: selectedTemplate.a4SpacingXMm ?? 2,
        spacingYMm: selectedTemplate.a4SpacingYMm ?? 2,
      };
    }
    return {};
  }, [selectedTemplate]);

  const a4Grid = useMemo(() => {
    if (!selectedTemplate) return null;
    return computeA4Grid(
      selectedTemplate.widthMm,
      selectedTemplate.heightMm,
      a4GridOptions,
    );
  }, [selectedTemplate, a4GridOptions]);

  const handleGenerate = async () => {
    if (!canGenerate) {
      toast.error("Select a product and a template first");
      return;
    }
    setGenerating(true);
    try {
      const label = await generateLabel({
        productId,
        templateId,
        batchNumber,
        manufacturingDate: manufacturingDate || null,
        packedDate: packedDate || null,
        expiryDate: expiryDate || null,
        bestBeforeDate: bestBeforeDate || null,
        copies,
      });
      toast.success("Label generated");
      return label;
    } catch (err) {
      toast.error("Failed to generate label");
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleExportPdf = async () => {
    const label = await handleGenerate();
    if (!label || !previewNodeRef.current || !selectedTemplate) return;
    try {
      if (copies > 1) {
        await exportCopiesAsA4Pdf(
          previewNodeRef.current,
          selectedTemplate.widthMm,
          selectedTemplate.heightMm,
          copies,
          a4GridOptions,
          `${selectedProduct?.productNameEn || "labels"}.pdf`,
        );
      } else {
        await exportSingleLabelPdf(
          previewNodeRef.current,
          selectedTemplate.widthMm,
          selectedTemplate.heightMm,
          `${selectedProduct?.productNameEn || "label"}.pdf`,
        );
      }
      await recordPrint({
        generatedLabelId: label.id,
        printerType: "EXPORT_PDF",
        copies,
        status: "SUCCESS",
      });
    } catch (err) {
      toast.error("Failed to export PDF");
    }
  };

  const handlePrint = async () => {
    const label = await handleGenerate();
    if (!label || !previewNodeRef.current || !selectedTemplate) return;
    try {
      if (copies > 1) {
        await printCopiesOnA4(
          previewNodeRef.current,
          selectedTemplate.widthMm,
          selectedTemplate.heightMm,
          copies,
          a4GridOptions,
        );
      } else {
        await printLabelNode(
          previewNodeRef.current,
          selectedTemplate.widthMm,
          selectedTemplate.heightMm,
        );
      }
      await recordPrint({
        generatedLabelId: label.id,
        printerType: "USB",
        copies,
        status: "SUCCESS",
      });
    } catch (err) {
      toast.error("Failed to print");
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Generate Labels
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
            <Autocomplete
              options={products}
              getOptionLabel={(p) =>
                `${p.productNameEn}${p.sku ? " (" + p.sku + ")" : ""}`
              }
              onChange={(e, val) => setProductId(val?.id || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Product"
                  size="small"
                  sx={{ mb: 2 }}
                />
              )}
            />

            <TextField
              select
              label="Select Template"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} ({t.widthMm}x{t.heightMm}mm)
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Background Color Override"
              type="color"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              value={
                backgroundColorOverride ||
                selectedTemplate?.backgroundColor ||
                "#ffffff"
              }
              onChange={(e) => setBackgroundColorOverride(e.target.value)}
            />

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Batch Number"
                  size="small"
                  fullWidth
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Copies"
                  type="number"
                  size="small"
                  fullWidth
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, Number(e.target.value)))}
                  helperText={
                    a4Grid
                      ? `${a4Grid.perPage} fit per A4 page (${a4Grid.columns} x ${a4Grid.rows} grid)`
                      : "Select a template to see layout"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Manufacturing Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={manufacturingDate}
                  onChange={(e) => setManufacturingDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Packed Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={packedDate}
                  onChange={(e) => setPackedDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Expiry Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Best Before"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={bestBeforeDate}
                  onChange={(e) => setBestBeforeDate(e.target.value)}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                disabled={!canGenerate || generating}
                onClick={handleExportPdf}
                fullWidth
              >
                Generate PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                disabled={!canGenerate || generating}
                onClick={handlePrint}
                fullWidth
              >
                Print
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Preview
            </Typography>
            {selectedTemplate ? (
              <div ref={previewNodeRef}>
                <LabelCanvas
                  widthMm={selectedTemplate.widthMm}
                  heightMm={selectedTemplate.heightMm}
                  backgroundColor={
                    backgroundColorOverride || selectedTemplate.backgroundColor
                  }
                  backgroundImageUrl={selectedTemplate.backgroundImageUrl}
                  backgroundImageOpacity={
                    selectedTemplate.backgroundImageOpacity
                  }
                  borderColor={selectedTemplate.borderColor}
                  borderWidth={selectedTemplate.borderWidth}
                  elements={elements}
                  dataMap={dataMap}
                  scale={2}
                />
              </div>
            ) : (
              <Typography color="text.secondary">
                Select a product and a template to preview the label.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GenerateLabel;
